import React, { useState } from 'react'
import PhoneInput, {
  getCountryCallingCode,
  getCountries,
  type Country,
} from 'react-phone-number-input'
import flags from 'react-phone-number-input/flags'

import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { ChevronDown } from 'lucide-react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'

import 'react-phone-number-input/style.css'
import { Button } from './ui/button'

interface PhoneNumberInputProps {
  value: string
  onChange: (value: string | undefined) => void
  placeholder?: string
  required?: boolean
  disabled?: boolean
  className?: string
  label?: string
  error?: string
}

export function PhoneNumberInput({
  value,
  onChange,
  placeholder = 'Enter phone number',
  required = false,
  disabled = false,
  className,
  label,
  error,
}: Readonly<PhoneNumberInputProps>) {
  const CustomCountrySelect = ({
    value: countryValue,
    onChange,
    disabled,
  }: {
    value?: Country
    onChange: (country: Country) => void
    disabled?: boolean
  }) => {
    const [open, setOpen] = useState(false)

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            size="sm"
            variant="ghost"
            disabled={disabled}
            className="flex items-center justify-between rounded-md p-0 gap-1 -me-1 hover:bg-transparent bg-white text-sm"
          >
            {countryValue ? (
              <span className="flex items-center gap-2 truncate">
                {/* ✅ Ensures flags always render with proper size */}
                {flags[countryValue] &&
                  React.createElement(flags[countryValue], {
                    title: countryValue,
                  })}
                <span>
                  {countryValue.toUpperCase()} +
                  {getCountryCallingCode(countryValue)}
                </span>
              </span>
            ) : (
              <span className="text-ss-neutral-400">Select</span>
            )}
            <ChevronDown className="size-4 shrink-0" />
          </Button>
        </PopoverTrigger>

        {/* ✅ Popover aligned to start */}
        <PopoverContent className="p-0 w-72" align="start" sideOffset={4}>
          <Command>
            <CommandInput placeholder="Search country..." />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                {getCountries().map(country => (
                  <CommandItem
                    key={country}
                    value={country}
                    onSelect={() => {
                      onChange(country)
                      setOpen(false)
                    }}
                  >
                    <div className="flex items-center gap-2">
                      {flags[country] &&
                        React.createElement(flags[country], {
                          title: country,
                        })}
                      <span className="flex-1">
                        {country.toUpperCase()} +
                        {getCountryCallingCode(country)}
                      </span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    )
  }

  return (
    <div className="space-y-2">
      {label && (
        <Label
          className="text-sm font-medium text-ss-neutral-950"
          variant={required ? 'required' : undefined}
        >
          {label}
        </Label>
      )}
      <PhoneInput
        countryCallingCodeEditable={false}
        defaultCountry="US"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        countrySelectComponent={CustomCountrySelect}
        className={cn(
          'phone-input-custom h-11 border border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20 rounded-md py-1 pe-3 hover:border-ss-blue-500',
          disabled && 'phone-input-disabled',
          error && 'phone-input-error',
          className
        )}
      />
      {error && <p className="text-sm text-ss-critical-200 mt-1">{error}</p>}
    </div>
  )
}
